import { BehaviorSubject } from 'rxjs';
import { ComparatorService } from '../services/comparator-service';

export class TableSortModel<T> {
  private readonly _tableId: string;
  private readonly _columns = new Map<string, ColumnSortModel<T>>();

  private readonly _sortedColumns = new BehaviorSubject<ColumnSortModel<T>>(undefined);
  public sortedColumns$ = this._sortedColumns.asObservable();

  constructor(
    tableId: string,
    protected comparatorService: ComparatorService
  ) {
    this._tableId = tableId;
  }

  get columns(): Map<string, ColumnSortModel<T>> {
    return this._columns;
  }

  addColumn(
    columnId: string,
    sortFunc: (a: T, b: T) => number = () => 0,
    backendSortEnum?: any,
    enableFunc: (columnId: string) => boolean = () => true
  ): ColumnSortModel<T> {
    const columnModel = this._columns.get(columnId);
    if (columnModel) {
      throw new Error('Column sort Model already exists');
    }
    // not able to call other internal functions via this otherwise
    const boundedContextSortFunc = sortFunc.bind(this.comparatorService);
    const columnSortModel = new ColumnSortModel<T>(columnId, this, boundedContextSortFunc, backendSortEnum, enableFunc);
    this._columns.set(columnId, columnSortModel);
    return columnSortModel;
  }

  setCurrentSortColumn(currentColumn: ColumnSortModel<T>) {
    Array.from(this._columns.values())
      .filter((m) => m.columnId !== currentColumn.columnId)
      .forEach((m) => m.resetSort());
    if (currentColumn.sortDirection !== SortDirection.NONE) {
      this._sortedColumns.next(currentColumn);
    } else {
      this._sortedColumns.next(undefined);
    }
  }
}

export class ColumnSortModel<T> {
  private readonly _columnId: string;
  private readonly tableSortModel: TableSortModel<T>;
  private readonly enableFunc: (columnId: string) => boolean;
  private readonly sortFunc: (a, b) => number;
  private readonly _sortEnumBackend: any;
  private _sortDirection = SortDirection.NONE;

  constructor(
    columnId: string,
    tableSortModel: TableSortModel<T>,
    sortFunc: (a, b) => number,
    backendSortEnum: any,
    enableFunc: (columnId: string) => boolean
  ) {
    this._columnId = columnId;
    this.tableSortModel = tableSortModel;
    this._sortEnumBackend = backendSortEnum;
    this.sortFunc = sortFunc;
    this.enableFunc = enableFunc;
  }

  get columnId(): string {
    return this._columnId;
  }

  resetSort() {
    this._sortDirection = SortDirection.NONE;
  }

  get active(): boolean {
    return this._sortDirection === SortDirection.ASC || this._sortDirection === SortDirection.DESC;
  }

  get classes(): string {
    return this.sortDirection;
  }

  get sortDirection(): SortDirection {
    return this._sortDirection;
  }

  get isOpen(): boolean {
    if (this._sortDirection === SortDirection.ASC) {
      return true;
    } else if (this._sortDirection === SortDirection.DESC) {
      return false;
    }
  }

  get sortDirectionBackend(): 'DESC' | 'ASC' {
    if (this.sortDirection === SortDirection.ASC) {
      return 'ASC';
    } else if (this.sortDirection === SortDirection.DESC) {
      return 'DESC';
    } else {
      return undefined;
    }
  }

  get sortEnumBackend(): any {
    return this._sortEnumBackend;
  }

  switchSortDirection() {
    if (!this.enableFunc(this.columnId)) {
      return;
    }
    if (this._sortDirection === SortDirection.NONE) {
      this.changeSortMode(SortDirection.ASC);
    } else if (this._sortDirection === SortDirection.ASC) {
      this.changeSortMode(SortDirection.DESC);
    } else if (this._sortDirection === SortDirection.DESC) {
      this.changeSortMode(SortDirection.ASC);
    } else {
      this.changeSortMode(SortDirection.ASC);
    }
  }

  changeSortMode(sortDirection: SortDirection) {
    if (!this.enableFunc(this.columnId) || !sortDirection) {
      return;
    }
    this._sortDirection = sortDirection;
    this.tableSortModel.setCurrentSortColumn(this);
  }

  sort(arrayToSort: any[]): any[] {
    if (!arrayToSort) {
      return arrayToSort;
    }
    if (this._sortDirection === SortDirection.ASC) {
      return arrayToSort.sort(this.sortFunc);
    } else if (this._sortDirection === SortDirection.DESC) {
      const res = arrayToSort.sort(this.sortFunc);
      return res.reverse();
    } else {
      return arrayToSort;
    }
  }
}

export enum SortDirection {
  ASC = 'sortingAsc',
  DESC = 'sortingDesc',
  NONE = 'sortingNone'
}
